import { ArrowLeft, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { TERM_LANGUAGES, TERMS_COPY, TERMS_DISCLAIMER } from "@/data/termsContent";
import "@/App.css";

const TermsPage = () => {
  const [activeLanguage, setActiveLanguage] = useState("en");
  const language = TERM_LANGUAGES.find((item) => item.id === activeLanguage) || TERM_LANGUAGES[0];
  const copy = TERMS_COPY[language.id];
  const isArabic = language.direction === "rtl";

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content");
    document.title = "Terms & Conditions | MyEnglish Lesson.ca";
    description?.setAttribute("content", "Read the official Terms & Conditions for MyEnglish Lesson.ca, including Stripe payment policies, Class Pass reward rules, and session schedules.");
    return () => {
      document.title = previousTitle;
      if (description) {
        if (previousDescription === null || previousDescription === undefined) description.removeAttribute("content");
        else description.setAttribute("content", previousDescription);
      }
    };
  }, []);

  const goToBookings = () => { window.location.href = "/#booking-section"; };

  return (
    <div className={`terms-page ${isArabic ? "terms-rtl-mode" : ""}`} dir={language.direction} lang={language.lang} data-testid="terms-page">
      <Header homePrefix="/" onBook={goToBookings} />
      <main>
        <section className="terms-hero" aria-labelledby="terms-hero-title">
          <div className="terms-shell terms-hero-inner">
            <a className="terms-back-link" href="/" data-testid="terms-back-home-link"><ArrowLeft size={15} aria-hidden="true" /> Back to MyEnglish Lesson.ca</a>
            <div className="section-kicker">TERMS &amp; POLICIES</div>
            <h1 id="terms-hero-title" data-testid="terms-hero-title">Terms &amp; Conditions</h1>
            <p className="terms-hero-lead">A clear reference for lessons, payments, attendance, and Class Pass rebates.</p>
          </div>
        </section>

        <div className="terms-tabs-bar" data-testid="terms-language-tabs">
          <div className="terms-shell terms-tabs-inner" role="tablist" aria-label="Choose terms language">
            {TERM_LANGUAGES.map((item) => (
              <button
                key={item.id}
                className={`terms-tab-btn ${activeLanguage === item.id ? "is-active" : ""}`}
                type="button"
                role="tab"
                aria-selected={activeLanguage === item.id}
                aria-controls="terms-content-panel"
                onClick={(event) => { event.preventDefault(); setActiveLanguage(item.id); }}
                data-testid={`terms-tab-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="terms-shell terms-main">
          <div className="terms-disclaimer" role="note" data-testid="terms-translation-disclaimer">
            <ShieldAlert size={22} aria-hidden="true" />
            <p>{TERMS_DISCLAIMER}</p>
          </div>

          <div className="terms-layout">
            <aside className="terms-toc" data-testid="terms-table-of-contents" aria-label="Terms table of contents">
              <span className="terms-toc-kicker">IN THIS DOCUMENT</span>
              <nav>
                {copy.sections.map((section) => (
                  <a key={section.id} href={`#terms-${section.id}`} data-testid={`terms-toc-${section.id}-link`}>{section.title}</a>
                ))}
              </nav>
            </aside>

            <article className="terms-content-panel" id="terms-content-panel" role="tabpanel" data-testid="terms-content-panel">
              <header className="terms-document-header">
                <span className="terms-document-language" data-testid="terms-active-language">{language.label}</span>
                <h2 id="terms-document-title" data-testid="terms-document-title">{copy.title}</h2>
                <p className="terms-document-updated" data-testid="terms-document-updated">{copy.updated}</p>
                <p className="terms-document-intro" data-testid="terms-document-intro">{copy.intro}</p>
              </header>

              <div className="terms-clause-list">
                {copy.sections.map((section) => (
                  <section className="terms-clause-card" id={`terms-${section.id}`} key={section.id} data-testid={section.testId}>
                    <h3>{section.title}</h3>
                    {section.blocks.map((block, blockIndex) => {
                      if (block.type === "paragraph") return <p key={`${section.id}-paragraph-${blockIndex}`}>{block.text}</p>;
                      return (
                        <ul key={`${section.id}-list-${blockIndex}`}>
                          {block.items.map((item) => {
                            const text = typeof item === "string" ? item : item.text;
                            return (
                              <li key={text}>
                                <span>{text}</span>
                                {typeof item !== "string" && item.numbered && <ol>{item.numbered.map((numberedItem) => <li key={numberedItem}>{numberedItem}</li>)}</ol>}
                              </li>
                            );
                          })}
                        </ul>
                      );
                    })}
                  </section>
                ))}
              </div>
            </article>
          </div>
        </div>
      </main>
      <Footer homePrefix="/" />
    </div>
  );
};

export default TermsPage;
